import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const AdminExams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const BASEURL = process.env.REACT_APP_API_URL
  

  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASEURL}/admin/exams`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch exams');
      }

      const data = await response.json();
      setExams(data.exams);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExams();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const formatted = formatDateTimeDDMMYYYY(dateString);
    // Remove seconds from display (only show DD.MM.YYYY HH:mm)
    return formatted ? formatted.substring(0, formatted.lastIndexOf(':')) : 'N/A';
  };

  const getEmailStatusColor = (sent, total) => {
    if (sent === 0) return 'error';
    if (sent === total) return 'success';
    return 'warning';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error" variant="h6">
          Error: {error}
        </Typography>
        <button onClick={fetchExams} style={{ padding: '8px 16px', margin: '8px' }}>
          🔄 Refresh
        </button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography 
          variant="h4" 
          component="h2"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700
          }}
        >
          Exam Management
        </Typography>
        <Tooltip title="Refresh">
          <button 
            onClick={fetchExams} 
            style={{ 
              padding: '0.75rem 1.5rem',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
            }}
          >
            🔄 Refresh
          </button>
        </Tooltip>
      </Box>

      <Box sx={{ 
        backgroundColor: 'white', 
        borderRadius: 2, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Exam ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Level</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Students</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Start Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Emails Sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exams.map((exam) => (
                <TableRow key={exam.exam_id} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" sx={{ color: '#666' }}>
                      {exam.exam_id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                      {exam.exam_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={exam.level} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 500,
                        borderColor: '#667eea',
                        color: '#667eea'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span style={{ fontSize: '1.2rem' }}>👥</span>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                        {exam.student_count}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {formatDate(exam.start_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span style={{ fontSize: '1.2rem' }}>📧</span>
                      <Chip
                        label={`${exam.emails_sent}/${exam.total_assignments}`}
                        size="small"
                        color={getEmailStatusColor(exam.emails_sent, exam.total_assignments)}
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {exams.length === 0 && (
        <Box textAlign="center" py={6}>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            📊 No exams found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Upload Olymp and Webshop files to create your first exam
          </Typography>
        </Box>
      )}

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="textSecondary">
          Total Exams: <strong>{exams.length}</strong>
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminExams; 